import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { getPaymentFBOType } from "../../../../utils/utils";
import "./FBOFilters.css";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";

const FboFilters = ({ onClickApply, onClickClear, bankConfig }) => {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [selectType, setSelectType] = useState("");
  const [openBankDetailsDialog, setOpenBankDetailsDialog] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [errorKey, setErrorKey] = useState(0);

  // Fee type options
  const feeTypeOptions = [
    { value: "LANDING_FEE", label: getPaymentFBOType("LANDING_FEE") },
    { value: "FUEL_ROYALTY", label: getPaymentFBOType("FUEL_ROYALTY") },
    { value: "LAND_LEASE", label: getPaymentFBOType("LAND_LEASE") },
  ];

  const setDefaultValue = () => {
    setSelectType("");
    setInvoiceNumber("");
  };

  const handleSelectTypeChange = (event) => {
    setSelectType(event.target.value);
  };

  const handleInvoiceNumberChange = (event) => {
    setInvoiceNumber(event.target.value);
  };

  const handleClearFilter = () => {
    setDefaultValue();
    if (onClickClear) {
      onClickClear();
    }
  };

  const handleApplyFilter = () => {
    // Check if at least one filter is selected
    const hasInvoiceNumber = invoiceNumber && invoiceNumber.trim() !== "";
    const hasSelectType = selectType && selectType.trim() !== "";

    if (!hasInvoiceNumber && !hasSelectType) {
      setErrorKey(prev => prev + 1);
      setShowValidationError(true);
      return;
    }

    // Clear any existing validation error
    setShowValidationError(false);

    if (onClickApply) {
      onClickApply(selectType, invoiceNumber);
    }
  };

  const handleCloseValidationError = () => {
    setShowValidationError(false);
  };

  return (
    <>
      {showValidationError && (
        <SnackbarAlert
          key={errorKey}
          type="error"
          onClose={handleCloseValidationError}
        >
          Please select at least one filter before applying
        </SnackbarAlert>
      )}
      <div className="fbo-filters-filter-container">
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FormControl variant="outlined">
                  <div className="fbo-filters-select-header">Invoice number</div>
                  <TextField
                    id="invoices-invoice-number"
                    variant="outlined"
                    placeholder="Enter here"
                    className="fbo-filters-textfield"
                    value={invoiceNumber}
                    onChange={handleInvoiceNumberChange}
                    InputProps={{
                      autoComplete: "off",
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl variant="outlined" className="fbo-filters-select">
                  <div className="fbo-filters-select-header">Select Fee Type</div>
                  <Select
                    value={selectType}
                    onChange={handleSelectTypeChange}
                    displayEmpty
                    className={`fbo-filters-select-field ${selectType === "" ? "empty-value" : ""}`}
                  >
                    <MenuItem value="" disabled>
                      Select Fee Type
                    </MenuItem>
                    {feeTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <div className="fbo-filters-button-container">
              <Button className="clear" onClick={handleClearFilter}>
                Clear
              </Button>
              <Button
                variant="contained"
                className="apply"
                onClick={handleApplyFilter}
              >
                Apply
              </Button>
              <Button
                variant="contained"
                color="secondary"
                className="bank-details"
                onClick={() => setOpenBankDetailsDialog(true)}

              >
                Bank details
              </Button>
            </div>
          </Grid>
        </Grid>
        <Dialog
          open={openBankDetailsDialog}
          onClose={() => setOpenBankDetailsDialog(false)}
        >
          <DialogContent>
            <div className="bank-details-container">
              <div className="bank-details-section">
                <h2>BSD Banking Details</h2>
                <div className="bank-details-content">
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Account Name: </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.bsdAccountName}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Bank Name:</span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.bsdBankName}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Branch Number: </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.bsdBranchNumber}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Account Number: </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.bsdAccountNumber}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bank-details-section">
                <h3>USD Banking Details</h3>
                <div className="bank-details-content">
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Intermediary Bank: </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdIntermediaryBank}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">ABA: </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdAba}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">SWIFT: </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdSwift}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">
                      For Credit to Account:
                    </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdCreditAccount}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Beneficiary Bank: </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdBeneficiaryBank}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">
                      Bahamas Transit Number:
                    </span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdBahamasTransitNumber}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Account Number:</span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdAccountNumber}
                    </span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">Beneficiary Name:</span>
                    <span className="bank-detail-value">
                      {bankConfig?.data?.usdBeneficiaryName}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="contained"
                color="secondaryButton"
                className="done-button"
                onClick={() => setOpenBankDetailsDialog(false)}
              >
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default FboFilters;
